import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import frLocale from "@fullcalendar/core/locales/fr";
import toast from "react-hot-toast";
import { useTheme } from "./ThemeContext";
import { fetchAuth } from "./api";
import { useTranslation } from "react-i18next";
import { useSeason, getSeasonRange, getCurrentSeasonLabel } from "./SeasonContext";
import { Trophy, Dumbbell, Moon, Activity, Heart, Brain, Zap, Clock, Lock, Cake } from "lucide-react";
import EventModal from "./calendar/EventModal";
import TrainingPlayersModal from "./calendar/TrainingPlayersModal";
import MatchCancelConfirmModal from "./calendar/MatchCancelConfirmModal";
import {
  storageSlotToInstant,
  instantToStorageSlot,
  addMinutes,
} from "./calendar/CalendarTimezone";

function getSessionStyle(label, type) {
  const l = String(label || "").toLowerCase();
  
  if (type === "rest" || /\brest\b|jour\s*off|day\s*off|congé|repos\s*complet/i.test(l)) {
    return {
      icon: Moon,
      bgLight: "bg-slate-400",
      bgDark: "bg-slate-500",
      borderLight: "border-slate-300",
      borderDark: "border-slate-400",
      textClass: "text-white",
      accentClass: "bg-slate-300/30",
    };
  }
  
  // Match
  if (type === "match" || /match|game|compétition|competition/i.test(l)) {
    return {
      icon: Trophy,
      bgLight: "bg-amber-500",
      bgDark: "bg-amber-600",
      borderLight: "border-amber-400",
      borderDark: "border-amber-500",
      textClass: "text-white",
      accentClass: "bg-amber-400/30",
    };
  }
  
  // Recovery
  if (/récup|recup|recovery|repos/i.test(l)) {
    return {
      icon: Heart,
      bgLight: "bg-emerald-500",
      bgDark: "bg-emerald-600",
      borderLight: "border-emerald-400",
      borderDark: "border-emerald-500",
      textClass: "text-white",
      accentClass: "bg-emerald-400/30",
    };
  }
  
  // Strength
  if (/force|strength|muscul|gym/i.test(l)) {
    return {
      icon: Dumbbell,
      bgLight: "bg-rose-500",
      bgDark: "bg-rose-600",
      borderLight: "border-rose-400",
      borderDark: "border-rose-500",
      textClass: "text-white",
      accentClass: "bg-rose-400/30",
    };
  }
  
  // Tactical
  if (/tactique|tactic|video|analyse/i.test(l)) {
    return {
      icon: Brain,
      bgLight: "bg-violet-500",
      bgDark: "bg-violet-600",
      borderLight: "border-violet-400",
      borderDark: "border-violet-500",
      textClass: "text-white",
      accentClass: "bg-violet-400/30",
    };
  }
  
  // Intense
  if (/intensif|intense|hiit|sprint/i.test(l)) {
    return {
      icon: Zap,
      bgLight: "bg-orange-500",
      bgDark: "bg-orange-600",
      borderLight: "border-orange-400",
      borderDark: "border-orange-500",
      textClass: "text-white",
      accentClass: "bg-orange-400/30",
    };
  }
  
  // Default: training
  return {
    icon: Activity,
    bgLight: "bg-sky-500",
    bgDark: "bg-sky-600",
    borderLight: "border-sky-400",
    borderDark: "border-sky-500",
    textClass: "text-white",
    accentClass: "bg-sky-400/30",
  };
}


const minutesToHHMM = (min) => {
  const m = Math.max(0, Math.min(23*60+59, Number(min ?? 0)));
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const makeIso = (date, hhmm) => `${date}T${hhmm}:00`;

const getDefaultStartMin = (type) => {
  if (type === "match") return 15 * 60;
  if (type === "training") return 10 * 60;
  return 8 * 60;
};

const getDefaultDuration = (type) => {
  if (type === "match") return 105;
  if (type === "training") return 90;
  return 60;
};


const hhmmToMinutes = (s) => {
  if (!s || typeof s !== "string" || !s.includes(":")) return null;
  const [hh, mm] = s.split(":").map((x) => Number(x));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

const safeParseYmd = (ymd) => new Date(`${ymd}T00:00:00`);

const ClubCalendar = ({ scope = "global", playerId = "", players = [] }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const { season } = useSeason();
  const calendarRef = useRef(null);

  const isPlayer = scope === "player";

  const [applyToPlayers, setApplyToPlayers] = useState([]);

  const [events, setEvents] = useState({});
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [eventPeriod, setEventPeriod] = useState("morning");

  const [selectedList, setSelectedList] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // Form
  const [eventType, setEventType] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [trainingLabel, setTrainingLabel] = useState("");
  const [trainingDescription, setTrainingDescription] = useState("");

  // microcycle-like timing
  const [startHHMM, setStartHHMM] = useState("");
  const [durationMin, setDurationMin] = useState(""); // string for input
  const [sessionTemplateId, setSessionTemplateId] = useState("");

  // Player management modal
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const [selectedEventForPlayers, setSelectedEventForPlayers] = useState(null);

  const [assignedPlayers, setAssignedPlayers] = useState([]);

  // 🆕 Match cancel confirmation modal
  const [matchCancelModal, setMatchCancelModal] = useState(null); // { item, notify? }
  const [cancelLoading, setCancelLoading] = useState(false);

  const wrapRef = useRef(null);

  const [birthdays, setBirthdays] = useState([]);

  const [calendarHeight, setCalendarHeight] = useState(500);
  
  useEffect(() => {
    const updateHeight = () => {
      if (wrapRef.current) {
        const rect = wrapRef.current.getBoundingClientRect();
        const availableHeight = Math.floor(rect.height);
        setCalendarHeight(Math.max(300, availableHeight - 8));
      }
    };
    
    updateHeight();
    
    const resizeObserver = new ResizeObserver(updateHeight);
    if (wrapRef.current) {
      resizeObserver.observe(wrapRef.current);
    }
    
    window.addEventListener('resize', updateHeight);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const getAnchorDate = (seasonLabel) => {
    const today = new Date();
    return seasonLabel === getCurrentSeasonLabel()
      ? today
      : getSeasonRange(seasonLabel).start;
  };

  const normalizeToList = (slot) => {
    if (!slot) return [];
    if (Array.isArray(slot)) return slot;
    if (typeof slot === "object") return [slot];
    if (typeof slot === "string") return [{ id: `legacy-${slot}`, type: slot }];
    return [];
  };

  const isItemFromOverride = (item) => {
    if (!isPlayer || !selectedDate || !item?.id) return false;
    const slot = overrides?.[selectedDate]?.[eventPeriod];
    if (!slot) return false;
    const list = Array.isArray(slot) ? slot : [];
    return list.some((e) => e?.id === item.id);
  };

  const reloadCalendar = async () => {
    const { start, end } = getSeasonRange(season);

    if (isPlayer) {
      if (!playerId) {
        setEvents({});
        setOverrides({});
        return;
      }
      const res = await fetchAuth(`/players/${playerId}/calendar?season=${season}`);
      if (!res.ok) throw new Error("Failed to fetch player calendar");
      const data = await res.json();

      const filtered = Object.fromEntries(
        Object.entries(data.events || {}).filter(([d]) => {
          const dateObj = safeParseYmd(d);
          return dateObj >= start && dateObj <= end;
        })
      );

      setEvents(filtered);
      setOverrides(data.overrides || {});
      return;
    }

    const res = await fetchAuth(`/calendar?season=${season}`);
    if (!res.ok) throw new Error("Failed to fetch global calendar");
    const data = await res.json();

    const filtered = Object.fromEntries(
      Object.entries(data.events || {}).filter(([d]) => {
        const dateObj = safeParseYmd(d);
        return dateObj >= start && dateObj <= end;
      })
    );

    setEvents(filtered);
    setOverrides({});
  };

  // Fetch player birthdays for the season
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchAuth(`/calendar/birthdays?season=${encodeURIComponent(season)}`);
        if (res.ok) {
          const data = await res.json();
          setBirthdays(data.birthdays || []);
        }
      } catch (e) {
        setBirthdays([]);
      }
    })();
  }, [season]);

  useEffect(() => {
    (async () => {
      try {
        await reloadCalendar();
        if (calendarRef.current) calendarRef.current.getApi().gotoDate(getAnchorDate(season));
      } catch (err) {
       
        toast.error("Error fetching calendar");
      }
    })();
  }, [season, scope, playerId]);

  useEffect(() => {
    if (!selectedDate) return;
    const slot = events?.[selectedDate]?.[eventPeriod];
    const list = normalizeToList(slot);
    setSelectedList(list);

    if (selectedItem && !list.some((e) => e?.id === selectedItem?.id)) {
      setSelectedItem(null);
    }
  }, [selectedDate, eventPeriod, events]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectedDate(null);
        resetForm();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ---------------- API ---------------- */

  const upsertGlobalEventApi = async (payload) => {
    const response = await fetchAuth(`/calendar/event?season=${season}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error || "Failed to save event");
    }
    return response.json();
  };

  const deleteGlobalEventApi = async (eventId, { notify = false } = {}) => {
    const url = `/calendar/event/${eventId}?season=${season}${notify ? "&notify=true" : ""}`;
    const response = await fetchAuth(url, {
      method: "DELETE",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error || "Failed to delete event");
    }
    return response.json();
  };

  const upsertPlayerOverrideApi = async (pid, payload) => {
    const response = await fetchAuth(`/players/${pid}/calendar/event?season=${season}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (response.status === 409) {
      const err = await response.json().catch(() => null);
      const msg = err?.error || "Collision";
      const c = err?.collision;
      const details = c
        ? ` (collision: ${minutesToHHMM(c.start_min)} + ${c.duration_min}min${c.label ? ` • ${c.label}` : ""})`
        : "";
      throw new Error(`${msg}${details}`);
    }

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error || `Failed for player ${pid}`);
    }
    return response.json();
  };

  const revertOverrideSlotApi = async ({ date, period }) => {
    const response = await fetchAuth(
      `/players/${playerId}/calendar/override?season=${season}&date=${date}&period=${period}`,
      { method: "DELETE" }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error || "Failed to revert override");
    }
    return response.json();
  };

  const removeForPlayerApi = async ({ date, period }) => {
    const response = await fetchAuth(`/players/${playerId}/calendar/remove?season=${season}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ date, period }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error || "Failed to remove for player");
    }
    return response.json();
  };

  /* ---------------- UI helpers ---------------- */

  const renderItemTitle = (it) => {
    if (!it) return "—";
    if (it.type === "match") return `⚽ Match${it?.opponent?.name ? ` vs ${it.opponent.name}` : ""}`;
    if (it.type === "training") return `🏋️ Training${it?.label ? ` — ${it.label}` : ""}`;
    if (it.type === "rest") return "😴 Rest";
    return it.type || "—";
  };

  const renderItemMeta = (it) => {
    if (!it) return "";
    const t = [];
    if (it.start_min !== undefined && it.start_min !== null) t.push(minutesToHHMM(it.start_min));
    if (it.duration_min !== undefined && it.duration_min !== null) t.push(`${it.duration_min}min`);
    return t.join(" • ");
  };

  const resetForm = () => {
    setEventType("");
    setOpponentName("");
    setTrainingLabel("");
    setTrainingDescription("");
    setStartHHMM("");
    setDurationMin("");
    setSessionTemplateId("");
    setSelectedItem(null);
    setApplyToPlayers([]);
    setAssignedPlayers([]);
  };

  const hydrateFormFromItem = (it) => {
    if (!it) return;

    setEventType(it.type || "");
    setOpponentName(it?.opponent?.name || "");
    setTrainingLabel(it?.label || "");
    setTrainingDescription(it?.description || "");
    setStartHHMM(minutesToHHMM(it?.start_min));
    setDurationMin(
      it?.duration_min !== undefined && it?.duration_min !== null ? String(it.duration_min) : ""
    );
    setSessionTemplateId(it?.session_template_id || "");
    setAssignedPlayers(it?.player_ids || []);
  };

  /* ---------------- UI handlers ---------------- */

  const handleDateClick = (info) => {
    setSelectedDate(info.dateStr);
    setEventPeriod("morning");
    setSelectedList([]);
    resetForm();
  };

  const handleEventClick = (clickInfo) => {
    clickInfo.jsEvent.preventDefault();
    const { date, period, list, raw, type } = clickInfo.event.extendedProps || {};

    // Ignore clicks on birthday events
    if (type === "birthday") return;
    
    if (clickInfo.jsEvent.ctrlKey && raw?.type === "training") {
      setSelectedEventForPlayers(raw);
      setShowPlayersModal(true);
      return;
    }
    
    if (!date || !period) return;

    setSelectedDate(date);
    setEventPeriod(period);
    setSelectedList(list || []);
    resetForm();
  };

  const handleDateClickTime = (info) => {
    const storageSlot = instantToStorageSlot(info.date);

    setSelectedDate(storageSlot.date);
    setEventPeriod(storageSlot.period);
    setStartHHMM(minutesToHHMM(storageSlot.start_min));
  };


  const buildPayloadFromForm = () => {
    const payload = { date: selectedDate, period: eventPeriod, type: eventType };

    const start_min = hhmmToMinutes(startHHMM);
    const duration_min_num = durationMin !== "" ? Number(durationMin) : null;

    if ((eventType === "training" || eventType === "match") && start_min !== null) {
      payload.start_min = start_min;
    }
    if ((eventType === "training" || eventType === "match") && Number.isFinite(duration_min_num)) {
      payload.duration_min = Math.max(1, Math.floor(duration_min_num));
    }

    if (eventType === "match") {
      payload.opponent_name = opponentName.trim();
    }

    if (eventType === "training") {
      payload.label = trainingLabel.trim();
      payload.description = trainingDescription || "";
      if (sessionTemplateId?.trim()) payload.session_template_id = sessionTemplateId.trim();
      
      if (assignedPlayers && assignedPlayers.length > 0) {
        payload.player_ids = assignedPlayers;
      }
      
      if (selectedItem?.parent_event_id) {
        payload.parent_event_id = selectedItem.parent_event_id;
      }
    }

    if (selectedItem?.id) payload.id = selectedItem.id;

    return payload;
  };

  const validateForm = () => {
    if (!selectedDate) return "No date selected";
    if (!eventType) return "Please select an event type";

    if (eventType === "match" && !opponentName.trim()) return "Opponent name required";
    if (eventType === "training" && !trainingLabel.trim()) return "Training label required";

    if (durationMin !== "" && !Number.isFinite(Number(durationMin))) return "Duration must be a number";
    if (durationMin !== "" && Number(durationMin) <= 0) return "Duration must be > 0";

    return null;
  };

  const handleSaveFromModal = async () => {
    const err = validateForm();
    if (err) return toast.error(err);
    if (saving) return;

    setSaving(true);

    try {
      const payload = buildPayloadFromForm();

      if (isPlayer) {
        if (!playerId) return toast.error("Select a player first");

        const targets = [String(playerId), ...applyToPlayers];

        const results = await Promise.allSettled(
          targets.map((pid) => {
            const p = { ...payload };
            if (pid !== String(playerId)) delete p.id;
            return upsertPlayerOverrideApi(pid, p);
          })
        );

        const okCount = results.filter((r) => r.status === "fulfilled").length;
        const fails = results
          .map((r, i) => (r.status === "rejected" ? { pid: targets[i], error: r.reason?.message } : null))
          .filter(Boolean);

        await reloadCalendar();

        if (fails.length === 0) toast.success(`Saved for ${okCount} player${okCount > 1 ? "s" : ""}`);
        else if (okCount > 0) {
          toast.success(`Saved for ${okCount}/${targets.length} players`);
          toast.error(`Failed for ${fails.length} player(s)`);
         
        } else {
          toast.error(fails[0]?.error || "Save error");
        }

        setApplyToPlayers([]);
      } else {
        await upsertGlobalEventApi(payload);
        await reloadCalendar();
        toast.success("Saved");
      }

      resetForm();
    } catch (e) {
    
      toast.error(e.message || "Save error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (!item?.id) return toast.error("Invalid event id");

    if (isPlayer) {
      if (!playerId || !selectedDate) return toast.error("Select a player first");

      if (!isItemFromOverride(item)) {
        return toast.error("This is a global activity. Use “Remove for player” or create an override.");
      }

      const ok = window.confirm("Delete this player override activity?");
      if (!ok) return;

      try {
        const slot = overrides?.[selectedDate]?.[eventPeriod];
        const list = Array.isArray(slot) ? slot : [];
        const remaining = list.filter((e) => e?.id !== item.id);

        await revertOverrideSlotApi({ date: selectedDate, period: eventPeriod });

        for (const ev of remaining) {
          const payload = {
            date: selectedDate,
            period: hhmmToMinutes(startHHMM) < 12*60 ? "morning" : "afternoon", // compat
            type: eventType,
            start_min: hhmmToMinutes(startHHMM),
            duration_min: Number(durationMin),
            ...(eventType==="training" ? { label, description } : {}),
            ...(eventType==="match" ? { opponent_name: matchLabel } : {}),
          };


          if (ev.type === "training") {
            payload.label = ev.label || "Training";
            payload.description = ev.description || "";
          }
          if (ev.type === "match") {
            payload.opponent_name = ev?.opponent?.name || "Opponent";
          }

          await upsertPlayerOverrideApi(String(playerId), payload);
        }

        await reloadCalendar();
        toast.success("Deleted");
        setSelectedItem(null);
      } catch (e) {
      
        toast.error(e.message || "Delete error");
      }
      return;
    }

    // Global mode — si c'est un match, on affiche la modale de confirmation
    if (item.type === "match") {
      setMatchCancelModal({ item });
      return;
    }

    const ok = window.confirm("Delete this activity?");
    if (!ok) return;

    try {
      await deleteGlobalEventApi(item.id);
      await reloadCalendar();
      toast.success("Deleted");
      setSelectedItem(null);
    } catch (e) {
      
      toast.error(e.message || "Delete error");
    }
  };

  const handleCancelMatch = (item) => {
    if (!item?.id || item.type !== "match") return;
    setMatchCancelModal({ item });
  };

  const handleConfirmMatchDelete = async (notify) => {
    const item = matchCancelModal?.item;
    if (!item?.id) return;

    setCancelLoading(true);
    try {
      const result = await deleteGlobalEventApi(item.id, { notify });
      await reloadCalendar();

      if (notify && result.notified_players > 0) {
        toast.success(`Match supprimé — ${result.notified_players} joueur(s) notifié(s)`);
      } else {
        toast.success("Match supprimé");
      }

      setSelectedItem(null);
      setMatchCancelModal(null);
    } catch (e) {
     
      toast.error(e.message || "Erreur lors de la suppression");
    } finally {
      setCancelLoading(false);
    }
  };

  /* ---------------- FullCalendar mapping ---------------- */

  const fcEvents = useMemo(() => {
    const out = [];
    
    const simultaneousGroups = {};
    
    for (const [date, day] of Object.entries(events || {})) {
      for (const period of ["morning", "afternoon"]) {
        const list = normalizeToList(day?.[period]);
        
        for (const it of list) {
          const startMin = it?.start_min ?? getDefaultStartMin(it?.type);
          const key = `${date}-${startMin}`;
          
          if (!simultaneousGroups[key]) {
            simultaneousGroups[key] = [];
          }
          simultaneousGroups[key].push({ ...it, date, period });
        }
      }
    }
    
    for (const [date, day] of Object.entries(events || {})) {
      for (const period of ["morning", "afternoon"]) {
        const list = normalizeToList(day?.[period]);
        
        for (const it of list) {
          const startMin = it?.start_min ?? getDefaultStartMin(it?.type);
          const dur = it?.duration_min ?? getDefaultDuration(it?.type);
          const start = storageSlotToInstant(date, startMin);
          const end = addMinutes(start, dur);
          
          const groupKey = `${date}-${startMin}`;
          const groupEvents = simultaneousGroups[groupKey] || [];
          const totalInGroup = groupEvents.length;
          const indexInGroup = groupEvents.findIndex(e => e.id === it.id);
          
          const isSimultaneous = totalInGroup > 1;
          const widthPercent = isSimultaneous ? Math.floor(100 / totalInGroup) : 100;
          const leftPercent = isSimultaneous ? indexInGroup * widthPercent : 0;
          
          const altGroup = it?.alternative_group || it?.parent_event_id;

          out.push({
            id: it.id,
            title:
              it.type === "match"
                ? `⚽ ${it?.opponent?.name ? `vs ${it.opponent.name}` : "Match"}`
                : it.type === "training"
                ? `🏋️ ${it?.label || "Training"}`
                : "😴 Rest",
            start,
            end,
            overlap: true,
            display: 'block',
            allDay: false,
            editable: true,
            extendedProps: {
              date,
              period,
              type: it.type,
              raw: it,
              list,
              playerCount: it?.player_ids?.length || 0,
              isAlternative: !!altGroup,
              isSimultaneous,
              totalInGroup,
              indexInGroup,
              widthPercent,
              leftPercent,
            },
          });
        }
      }
    }

    // Add birthday events as all-day
    for (const b of birthdays) {
      out.push({
        id: `birthday-${b.player_id}-${b.date}`,
        title: `🎂 ${b.first_name} ${b.last_name} (${b.age} ans)`,
        start: b.date,
        allDay: true,
        display: "block",
        editable: false,
        extendedProps: {
          type: "birthday",
          raw: b,
        },
      });
    }

    return out;
  }, [events, birthdays]);

  const renderEventContentMicrocycle = useCallback((arg) => {
    const raw = arg.event.extendedProps?.raw;
    const type = arg.event.extendedProps?.type || raw?.type;

    // 🎂 Birthday rendering
    if (type === "birthday") {
      const b = raw;
      return (
        <div
          className={`
            flex items-center gap-1.5 px-2 py-0.5 rounded-full truncate w-full
            bg-gradient-to-r from-pink-400 via-fuchsia-400 to-purple-400
            text-white shadow-sm
          `}
          title={`🎂 ${b.first_name} ${b.last_name} – ${b.age} ans – ${b.position || ""}`}
        >
          <Cake size={13} className="text-white shrink-0 drop-shadow" />
          <span className="text-[11px] font-semibold truncate">
            {b.first_name} {b.last_name}
          </span>
          <span className="text-[10px] font-medium opacity-80 ml-auto shrink-0">
            {b.age} ans
          </span>
        </div>
      );
    }

    const eventLabel = raw?.label || raw?.opponent?.name || type;
    const playerCount = arg.event.extendedProps?.playerCount || 0;
    const isAlternative = arg.event.extendedProps?.isAlternative || false;
    
    const isSimultaneous = arg.event.extendedProps?.isSimultaneous || false;
    const totalInGroup = arg.event.extendedProps?.totalInGroup || 1;
    const indexInGroup = arg.event.extendedProps?.indexInGroup || 0;

    // ── Past-match result detection ──
    const eventDate = arg.event.extendedProps?.date;
    const todayStr = new Date().toISOString().slice(0, 10);
    const isPastMatch = type === "match" && eventDate && eventDate < todayStr;
    const score = raw?.score;
    let matchResult = null; // 'win' | 'loss' | 'draw'
    if (isPastMatch && score && typeof score.home === "number" && typeof score.away === "number") {
      const clubIsHome = raw?.is_home !== false;
      const clubScore = clubIsHome ? score.home : score.away;
      const oppScore  = clubIsHome ? score.away  : score.home;
      if (clubScore > oppScore)      matchResult = "win";
      else if (clubScore < oppScore) matchResult = "loss";
      else                           matchResult = "draw";
    }

    const resultStyleMap = {
      win:  { bgLight: "bg-emerald-600", bgDark: "bg-emerald-700", borderLight: "border-emerald-500", borderDark: "border-emerald-600", accentClass: "bg-emerald-300/40" },
      loss: { bgLight: "bg-red-600",     bgDark: "bg-red-700",     borderLight: "border-red-500",     borderDark: "border-red-600",     accentClass: "bg-red-300/40" },
      draw: { bgLight: "bg-slate-500",   bgDark: "bg-slate-600",   borderLight: "border-slate-400",   borderDark: "border-slate-500",   accentClass: "bg-slate-300/40" },
    };
    const baseStyle = getSessionStyle(eventLabel, type);
    const style = matchResult ? { ...baseStyle, ...resultStyleMap[matchResult] } : baseStyle;
    const IconComponent = style.icon;

    const viewType = arg.view?.type || "";
    const isMonthView = viewType === "dayGridMonth";

    const start = arg.event.start;
    const end = arg.event.end;
    const startMin = start ? start.getHours() * 60 + start.getMinutes() : null;
    const dur = start && end ? Math.max(1, Math.round((end - start) / 60000)) : null;

    // Show score from club's perspective: club score first, opponent second
    const scoreStr = (() => {
      if (!score || typeof score.home !== "number" || typeof score.away !== "number") return null;
      const _clubIsHome = raw?.is_home !== false;
      const _clubScore = _clubIsHome ? score.home : score.away;
      const _oppScore  = _clubIsHome ? score.away  : score.home;
      return `${_clubScore}–${_oppScore}`;
    })();
    const subtitle =
      type === "match"
        ? `${raw?.opponent?.name ? `vs ${raw.opponent.name}` : "Match"}${scoreStr ? ` · ${scoreStr}` : ""}`
        : type === "training"
        ? (raw?.label || "Training")
        : "Rest";

    const isTMSource = raw?.source === "transfermarkt";

    if (isMonthView) {
      return (
        <div
          className={`
            flex items-center gap-1 px-1.5 py-0.5 rounded-md truncate w-full
            ${isDarkMode ? style.bgDark : style.bgLight}
            ${style.textClass}
          `}
          title={`${subtitle}${startMin != null ? ` • ${minutesToHHMM(startMin)}` : ""}${dur ? ` • ${dur}min` : ""}`}
        >
          <IconComponent size={11} className="text-white/90 shrink-0" />
          <span className="text-[10px] font-medium truncate">{subtitle}</span>
          {isTMSource && (
            <span className="text-[8px] font-bold opacity-60 ml-1 shrink-0 bg-black/20 px-0.5 rounded">TM</span>
          )}
          {matchResult && (
            <span className={`text-[8px] font-bold ml-1 shrink-0 px-1 rounded ${
              matchResult === "win" ? "bg-white/30" : matchResult === "loss" ? "bg-black/30" : "bg-white/20"
            }`}>
              {matchResult === "win" ? "✓" : matchResult === "loss" ? "✗" : "="}
            </span>
          )}
          {startMin != null && (
            <span className="text-[9px] opacity-70 ml-auto shrink-0">{minutesToHHMM(startMin)}</span>
          )}
        </div>
      );
    }

    const isUltraCompact = dur && dur < 30;
    const isCompact = dur && dur >= 30 && dur < 60;
    const isMedium = dur && dur >= 60 && dur < 90;

    const isMatch = type === "match";
    
    const simultaneousBorderColors = [
      'border-l-sky-400',
      'border-l-violet-400',
      'border-l-emerald-400',
      'border-l-amber-400',
      'border-l-rose-400',
    ];
    const borderColor = isSimultaneous ? simultaneousBorderColors[indexInGroup % simultaneousBorderColors.length] : '';

    return (
      <div 
        className={`
          relative w-full h-full rounded-lg overflow-hidden
          ${isDarkMode ? style.bgDark : style.bgLight}
          ${style.textClass}
          shadow-md hover:shadow-lg hover:brightness-110
          transition-all duration-150
          ${isMatch ? "ring-1 ring-amber-300/50" : ""}
          ${isAlternative ? "ring-2 ring-violet-400/60" : ""}
          ${isSimultaneous ? `border-l-4 ${borderColor}` : ""}
        `}
        title={`${subtitle}${playerCount > 0 ? ` • ${playerCount} joueur${playerCount > 1 ? 's' : ''}` : ''}${isSimultaneous ? ` • Groupe ${indexInGroup + 1}/${totalInGroup}` : ''}`}
      >
        {/* Accent stripe (seulement si pas simultané) */}
        {!isSimultaneous && (
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.accentClass}`} />
        )}
        
        {/* Badge groupe si simultané */}
        {isSimultaneous && (
          <div className={`absolute top-0.5 left-0.5 w-4 h-4 flex items-center justify-center rounded text-[8px] font-bold z-10 ${
            isDarkMode ? 'bg-black/50' : 'bg-white/70'
          }`}>
            {indexInGroup + 1}
          </div>
        )}
        
        {/* Lock for matches OR Players indicator for trainings */}
        {isMatch ? (
          <div className="absolute top-1 right-1 p-0.5 rounded-full bg-black/30 z-10">
            <Lock size={10} className="text-white/90" />
          </div>
        ) : playerCount > 0 ? (
          <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-black/40 z-10 text-[9px] font-bold">
            {playerCount}
          </div>
        ) : null}

        {/* Transfermarkt source badge */}
        {isTMSource && (
          <div className="absolute bottom-0.5 left-1.5 text-[8px] font-bold opacity-55 bg-black/20 px-1 rounded z-10 leading-tight">
            TM
          </div>
        )}

        {/* Match result badge (win / loss / draw) */}
        {matchResult && !isUltraCompact && (
          <div className={`absolute bottom-0.5 right-1 text-[9px] font-bold z-10 ${
            matchResult === "win"  ? "text-white" :
            matchResult === "loss" ? "text-white/80" :
            "text-white/70"
          }`}>
            {matchResult === "win" ? "✓" : matchResult === "loss" ? "✗" : "="}
          </div>
        )}

        {isUltraCompact ? (
          <div className="flex items-center justify-center h-full pl-2">
            <IconComponent size={12} className="text-white/90" />
          </div>
        ) : isCompact ? (
          <div className="flex items-center gap-1.5 h-full pl-2 pr-1">
            <IconComponent size={12} className="text-white/90 shrink-0" />
            <span className="text-[10px] font-medium truncate">{subtitle}</span>
            {playerCount > 0 && <span className="text-[9px] opacity-80">({playerCount})</span>}
          </div>
        ) : isMedium ? (
          <div className="flex flex-col justify-center h-full pl-2.5 pr-1 py-1 gap-0.5">
            <div className="flex items-center gap-1.5">
              <div className={`p-1 rounded ${style.accentClass}`}>
                <IconComponent size={12} className="text-white/90" />
              </div>
              <span className="text-xs font-semibold truncate">{subtitle}</span>
              {playerCount > 0 && <span className="text-[10px] opacity-70">({playerCount})</span>}
            </div>
            {startMin != null && dur != null && (
              <div className="text-[10px] opacity-80 flex items-center gap-1 pl-0.5">
                <Clock size={10} />
                {minutesToHHMM(startMin)} · {dur}′
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col justify-start h-full pl-2.5 pr-1 py-1.5 gap-1">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-md ${style.accentClass}`}>
                <IconComponent size={14} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate flex items-center gap-1">
                  {subtitle}
                  {playerCount > 0 && <span className="text-[10px] opacity-70">({playerCount})</span>}
                </div>
                {raw?.description && (
                  <div className="text-[10px] opacity-70 truncate">{raw.description}</div>
                )}
              </div>
            </div>
            {startMin != null && dur != null && (
              <div className="text-[10px] opacity-80 flex items-center gap-1 mt-auto">
                <Clock size={10} />
                {minutesToHHMM(startMin)} · {dur} min
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [isDarkMode]);

  const handleSelectSlot = (info) => {
    // info.startStr / info.endStr = datetime ISO
    const start = new Date(info.start);
    const end = new Date(info.end);

    const ymd = info.startStr.slice(0, 10);
    const startMin = start.getHours() * 60 + start.getMinutes();
    const dur = Math.max(15, Math.round((end - start) / 60000));

    setSelectedDate(ymd);
    setStartHHMM(minutesToHHMM(startMin));
    setDurationMin(String(dur));
    setEventType("");
    setSelectedItem(null);
  };

  const updateFromFcChange = async (fcEvent, oldEvent = null) => {
    const { date: originalDate, period: originalPeriod, raw, type } = fcEvent.extendedProps;
    const start = new Date(fcEvent.start);
    const end = new Date(fcEvent.end);

    const storageStart = instantToStorageSlot(fcEvent.start);

    const newYmd = storageStart.date;
    const newStartMin = storageStart.start_min;
    const newPeriod = storageStart.period;
    const dur = Math.max(15, Math.round((end - start) / 60000));

    const dateChanged = originalDate !== newYmd;
    const periodChanged = originalPeriod !== newPeriod;
    const slotChanged = dateChanged || periodChanged;

    if (slotChanged && !isPlayer) {
      try {
        await deleteGlobalEventApi(fcEvent.id);
      } catch (e) {
        throw new Error("Failed to move event ");
      }
    }

    const payload = {
      ...(slotChanged ? {} : { id: fcEvent.id }),
      date: newYmd,
      period: newPeriod,
      type,
      start_min: newStartMin,
      duration_min: dur,
    };

    if (type === "training") {
      payload.label = raw?.label || "Training";
      payload.description = raw?.description || "";
      if (raw?.player_ids) {
        payload.player_ids = raw.player_ids;
      }
      // Conserver l'alternative group si présent
      if (raw?.alternative_group) {
        payload.alternative_group = raw.alternative_group;
      }
      if (raw?.parent_event_id) {
        payload.parent_event_id = raw.parent_event_id;
      }
    }
    if (type === "match") {
      payload.opponent_name = raw?.opponent?.name || "Opponent";
    }

    if (isPlayer) {
      await upsertPlayerOverrideApi(playerId, payload);
    } else {
      await upsertGlobalEventApi(payload);
    }

    await reloadCalendar();
  };

  const handleEventDrop = async (info) => {
    try { 
      await updateFromFcChange(info.event, info.oldEvent); 
    }
    catch (e) { 
      toast.error(e.message); 
      info.revert(); 
    }
  };

  const handleEventResize = async (info) => {
    try { 
      await updateFromFcChange(info.event); 
    }
    catch (e) { 
      toast.error(e.message); 
      info.revert(); 
    }
  };

  /* ---------------- Player Management ---------------- */

  const handleSavePlayersList = async (eventId, playerIds) => {
    const response = await fetchAuth(`/calendar/event/${eventId}/players?season=${season}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ player_ids: playerIds }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error || "Failed to update players");
    }

    await reloadCalendar();
    return response.json();
  };

  const handleCreateAlternative = async (parentEvent) => {
    // Fermer le modal des joueurs
    setShowPlayersModal(false);
    
    // Ouvrir le modal principal avec les données pré-remplies
    setSelectedDate(parentEvent.date || selectedDate);
    setEventPeriod(parentEvent.period || eventPeriod);
    setEventType("training");
    setTrainingLabel(`${parentEvent.label || "Training"} (Alt)`);
    setTrainingDescription(parentEvent.description || "");
    setStartHHMM(minutesToHHMM(parentEvent.start_min));
    setDurationMin(String(parentEvent.duration_min || "90"));
    setSelectedItem({ ...parentEvent, id: null, parent_event_id: parentEvent.id });
  };

  /* ---------------- End Player Management ---------------- */



  return (
    <div className={`h-full flex flex-col overflow-hidden ${isDarkMode ? "bg-gray-950 text-white" : "bg-slate-50 text-gray-900"}`}>
      {/* Calendar Container - prend tout l'espace */}
      <div 
        ref={wrapRef}
        className="flex-1 min-h-0 overflow-hidden"
      >
        <div 
          className={`rounded-xl border shadow-md h-full overflow-hidden ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
          style={{
            "--fc-border-color": isDarkMode ? "rgba(55, 65, 81, 0.5)" : "rgba(229, 231, 235, 0.8)",
            "--fc-today-bg-color": isDarkMode ? "rgba(59, 130, 246, 0.08)" : "rgba(59, 130, 246, 0.05)",
            "--fc-now-indicator-color": "#ef4444",
            "--fc-event-border-color": "transparent",
            "--fc-page-bg-color": "transparent",
          }}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            timeZone="local"
            nowIndicator

            slotMinTime="06:00:00"
            slotMaxTime="23:59:00"

            slotDuration="01:00:00"
            snapDuration="00:15:00"
            slotLabelInterval="01:00:00"
            slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}

            allDaySlot={true}
            allDayText=""
            firstDay={1}
            height={calendarHeight}
            stickyHeaderDates
            dayMaxEvents={4}

            editable
            eventResizableFromStart
            eventDurationEditable
            eventStartEditable
            
            slotEventOverlap={false}
            eventMaxStack={5}

            eventClassNames={(arg) => {
              const classes = ["fc-modern-event"];
              if (arg.event.extendedProps?.isSimultaneous) {
                classes.push("fc-simultaneous-event");
              }
              return classes;
            }}
            eventContent={renderEventContentMicrocycle}
            eventDidMount={(info) => {
              const ext = info.event.extendedProps || {};
              const viewType = info.view?.type || "";
              // Skip simultaneous positioning in month view
              if (viewType === "dayGridMonth") return;
              if (ext.isSimultaneous && ext.totalInGroup > 1) {
                const total = ext.totalInGroup;
                const idx = ext.indexInGroup;
                // Forcer le positionnement sur l'élément wrapper de FullCalendar
                info.el.style.width = `calc(${100 / total}% - 2px)`;
                info.el.style.left = `calc(${(100 / total) * idx}% + ${idx}px)`;
                info.el.style.right = 'auto';
              }
            }}

            dateClick={handleDateClickTime}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            events={fcEvents}

            // Header styling
            locale={i18n.language?.startsWith("fr") ? frLocale : "en"}
            buttonText={{
              today: t("calendar_ui.today"),
              month: t("calendar_ui.month"),
              week: t("calendar_ui.week"),
              day: t("calendar_ui.day"),
            }}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay"
            }}
          />
        </div>
      </div>

      {/* Modal */}
      {selectedDate && (
        <EventModal
          isDarkMode={isDarkMode}
          isPlayer={isPlayer}
          selectedDate={selectedDate}
          eventPeriod={eventPeriod}
          startHHMM={startHHMM}
          selectedList={selectedList}
          selectedItem={selectedItem}
          eventType={eventType}
          opponentName={opponentName}
          trainingLabel={trainingLabel}
          trainingDescription={trainingDescription}
          durationMin={durationMin}
          saving={saving}
          playerId={playerId}
          players={players}
          applyToPlayers={applyToPlayers}
          onClose={() => {
            resetForm();
            setSelectedDate(null);
          }}
          onItemClick={(it) => {
            setSelectedItem(it);
            hydrateFormFromItem(it);
          }}
          onItemDelete={handleDeleteItem}
          onTypeSelect={(type, defaultDuration) => {
            setEventType(type);
            setSelectedItem(null);
            setDurationMin(defaultDuration);
            if (type === "training" && players.length > 0) {
              const allPlayerIds = players.map(p => p.id);
              setAssignedPlayers(allPlayerIds);
            } else {
              setAssignedPlayers([]);
            }
          }}
          setStartHHMM={setStartHHMM}
          setEventPeriod={setEventPeriod}
          setDurationMin={setDurationMin}
          setOpponentName={setOpponentName}
          setTrainingLabel={setTrainingLabel}
          setTrainingDescription={setTrainingDescription}
          setApplyToPlayers={setApplyToPlayers}
          onSave={handleSaveFromModal}
          onDelete={() => handleDeleteItem(selectedItem)}
          isItemFromOverride={isItemFromOverride}
          renderItemTitle={renderItemTitle}
          renderItemMeta={renderItemMeta}
          hhmmToMinutes={hhmmToMinutes}
          assignedPlayers={assignedPlayers}
          setAssignedPlayers={setAssignedPlayers}
          onSavePlayersList={handleSavePlayersList}
          onCancelMatch={handleCancelMatch}
        />
      )}

      {/* Players Modal */}
      {showPlayersModal && selectedEventForPlayers && (
        <TrainingPlayersModal
          isDarkMode={isDarkMode}
          event={selectedEventForPlayers}
          allPlayers={players}
          onClose={() => {
            setShowPlayersModal(false);
            setSelectedEventForPlayers(null);
          }}
          onSavePlayersList={handleSavePlayersList}
          onCreateAlternative={handleCreateAlternative}
        />
      )}

      {/* 🆕 Match cancel confirmation modal */}
      {matchCancelModal && (
        <MatchCancelConfirmModal
          isDarkMode={isDarkMode}
          matchLabel={
            matchCancelModal.item?.opponent?.name
              ? `vs ${matchCancelModal.item.opponent.name}`
              : "Match"
          }
          matchDate={selectedDate || ""}
          loading={cancelLoading}
          onConfirmWithNotify={() => handleConfirmMatchDelete(true)}
          onConfirmWithoutNotify={() => handleConfirmMatchDelete(false)}
          onCancel={() => setMatchCancelModal(null)}
        />
      )}
    </div>
  );
};

export default ClubCalendar;
